package com.example.hukakmp.ui

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.lazy.grid.GridCells
import androidx.compose.foundation.lazy.grid.LazyVerticalGrid
import androidx.compose.foundation.lazy.grid.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.FilterChip
import androidx.compose.material3.LinearProgressIndicator
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontStyle
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.example.hukakmp.model.EspecieConEstado
import com.example.hukakmp.viewmodel.PescadexViewModel

private enum class PescadexFiltro(val label: String) {
    TODAS("Todas"),
    CAPTURADAS("Capturadas"),
    POR_DESCUBRIR("Por descubrir"),
}

@Composable
fun PescadexScreen(
    uiState: PescadexViewModel.UiState,
    onBack: () -> Unit,
    onRefresh: () -> Unit,
) {
    var filtro by remember { mutableStateOf(PescadexFiltro.TODAS) }
    var query by remember { mutableStateOf("") }
    var seleccionada by remember { mutableStateOf<EspecieConEstado?>(null) }

    val visibles = remember(uiState.especies, filtro, query) {
        uiState.especies
            .asSequence()
            .filter {
                when (filtro) {
                    PescadexFiltro.TODAS -> true
                    PescadexFiltro.CAPTURADAS -> it.esCapturada
                    PescadexFiltro.POR_DESCUBRIR -> !it.esCapturada
                }
            }
            .filter {
                query.isBlank() ||
                    it.info.nombreComun.contains(query, ignoreCase = true) ||
                    it.info.nombreCientifico.contains(query, ignoreCase = true) ||
                    it.info.habitat.contains(query, ignoreCase = true)
            }
            .sortedWith(
                compareByDescending<EspecieConEstado> { it.esCapturada }
                    .thenBy { it.info.nombreComun },
            )
            .toList()
    }

    Column(
        modifier = Modifier
            .fillMaxSize()
            .background(MaterialTheme.colorScheme.background),
    ) {
        Surface(
            modifier = Modifier.fillMaxWidth(),
            color = MaterialTheme.colorScheme.primary,
            shadowElevation = 4.dp,
        ) {
            Row(
                modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp, vertical = 14.dp),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Text(
                    "‹",
                    color = MaterialTheme.colorScheme.onPrimary,
                    fontSize = 32.sp,
                    modifier = Modifier.clickable(onClick = onBack).padding(end = 12.dp),
                )
                Column(modifier = Modifier.weight(1f)) {
                    Text(
                        "Pescadex",
                        color = MaterialTheme.colorScheme.onPrimary,
                        fontWeight = FontWeight.Bold,
                        fontSize = 18.sp,
                    )
                    Text(
                        "Tu colección de especies",
                        color = MaterialTheme.colorScheme.onPrimary.copy(alpha = 0.8f),
                        style = MaterialTheme.typography.bodySmall,
                    )
                }
                Text(
                    "↻",
                    color = MaterialTheme.colorScheme.onPrimary,
                    fontSize = 24.sp,
                    modifier = Modifier.clickable(onClick = onRefresh).padding(8.dp),
                )
            }
        }

        if (uiState.isLoading) {
            Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                Column(horizontalAlignment = Alignment.CenterHorizontally) {
                    CircularProgressIndicator()
                    Spacer(Modifier.height(12.dp))
                    Text("Cargando tu Pescadex…")
                }
            }
            return@Column
        }

        uiState.error?.let {
            Surface(
                modifier = Modifier.fillMaxWidth().padding(16.dp),
                color = MaterialTheme.colorScheme.errorContainer,
                shape = RoundedCornerShape(14.dp),
            ) {
                Text(
                    it,
                    modifier = Modifier.padding(14.dp),
                    color = MaterialTheme.colorScheme.onErrorContainer,
                )
            }
        }

        PescadexHeader(uiState)

        OutlinedTextField(
            value = query,
            onValueChange = { query = it },
            label = { Text("Buscar especie") },
            placeholder = { Text("Pejerrey, róbalo, trucha…") },
            singleLine = true,
            modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp, vertical = 8.dp),
            shape = RoundedCornerShape(14.dp),
        )

        Row(
            modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp),
            horizontalArrangement = Arrangement.spacedBy(8.dp),
        ) {
            PescadexFiltro.entries.forEach { item ->
                FilterChip(
                    selected = filtro == item,
                    onClick = { filtro = item },
                    label = { Text(item.label) },
                )
            }
        }

        LazyVerticalGrid(
            columns = GridCells.Adaptive(minSize = 170.dp),
            modifier = Modifier.fillMaxSize(),
            contentPadding = androidx.compose.foundation.layout.PaddingValues(16.dp),
            horizontalArrangement = Arrangement.spacedBy(12.dp),
            verticalArrangement = Arrangement.spacedBy(12.dp),
        ) {
            items(visibles, key = { it.info.id }) { especie ->
                PescadexSpeciesCard(
                    especie = especie,
                    onClick = { seleccionada = especie },
                )
            }
        }
    }

    seleccionada?.let { especie ->
        PescadexDetailDialog(
            especie = especie,
            onDismiss = { seleccionada = null },
        )
    }
}

@Composable
private fun PescadexHeader(uiState: PescadexViewModel.UiState) {
    val stats = uiState.estadisticas
    Surface(
        modifier = Modifier.fillMaxWidth().padding(16.dp),
        shape = RoundedCornerShape(20.dp),
        color = MaterialTheme.colorScheme.primaryContainer,
    ) {
        Column(modifier = Modifier.padding(18.dp)) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.Bottom,
            ) {
                Column {
                    Text(
                        "Tu progreso",
                        style = MaterialTheme.typography.titleMedium,
                        fontWeight = FontWeight.Bold,
                    )
                    Text(
                        "${stats.especiesDescubiertas} de ${stats.totalEspecies} especies",
                        style = MaterialTheme.typography.bodySmall,
                        color = MaterialTheme.colorScheme.onPrimaryContainer.copy(alpha = 0.8f),
                    )
                }
                Text(
                    "${stats.porcentajeCompletado.toInt()}%",
                    fontSize = 28.sp,
                    fontWeight = FontWeight.Black,
                    color = MaterialTheme.colorScheme.primary,
                )
            }

            LinearProgressIndicator(
                progress = { (stats.porcentajeCompletado / 100f).coerceIn(0f, 1f) },
                modifier = Modifier.fillMaxWidth().padding(top = 12.dp),
                color = MaterialTheme.colorScheme.secondary,
            )

            Text(
                "${stats.totalCapturas} capturas acumuladas",
                style = MaterialTheme.typography.labelMedium,
                modifier = Modifier.padding(top = 10.dp),
                color = MaterialTheme.colorScheme.onPrimaryContainer,
            )
        }
    }
}

@Composable
private fun PescadexSpeciesCard(
    especie: EspecieConEstado,
    onClick: () -> Unit,
) {
    val accent = if (especie.esCapturada) Color(0xFF1D9E75) else MaterialTheme.colorScheme.outline
    Surface(
        modifier = Modifier
            .fillMaxWidth()
            .height(180.dp)
            .clickable(onClick = onClick),
        shape = RoundedCornerShape(18.dp),
        color = if (especie.esCapturada) {
            Color(0xFF1D9E75).copy(alpha = 0.12f)
        } else {
            MaterialTheme.colorScheme.surfaceVariant.copy(alpha = 0.42f)
        },
        tonalElevation = if (especie.esCapturada) 2.dp else 0.dp,
    ) {
        Column(
            modifier = Modifier.fillMaxSize().padding(14.dp),
            verticalArrangement = Arrangement.SpaceBetween,
        ) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
            ) {
                Text(
                    if (especie.esCapturada) "🐟" else "？",
                    fontSize = 32.sp,
                )
                Text(
                    if (especie.esCapturada) "DESCUBIERTA" else "POR DESCUBRIR",
                    style = MaterialTheme.typography.labelSmall,
                    fontWeight = FontWeight.Bold,
                    color = accent,
                )
            }

            Column {
                Text(
                    especie.info.nombreComun,
                    fontWeight = FontWeight.Bold,
                    style = MaterialTheme.typography.titleMedium,
                    maxLines = 2,
                )
                Text(
                    especie.info.nombreCientifico,
                    style = MaterialTheme.typography.bodySmall,
                    fontStyle = FontStyle.Italic,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                    maxLines = 1,
                )

                especie.datosCaptura?.let { captura ->
                    Text(
                        "${captura.totalCapturas} capturas",
                        style = MaterialTheme.typography.labelMedium,
                        color = Color(0xFF1D9E75),
                        fontWeight = FontWeight.Bold,
                        modifier = Modifier.padding(top = 7.dp),
                    )
                    if (captura.mejorDiaCantidad > 0) {
                        Text(
                            "Mejor jornada: ${captura.mejorDiaCantidad}",
                            style = MaterialTheme.typography.labelSmall,
                            color = MaterialTheme.colorScheme.onSurfaceVariant,
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun PescadexDetailDialog(
    especie: EspecieConEstado,
    onDismiss: () -> Unit,
) {
    AlertDialog(
        onDismissRequest = onDismiss,
        title = {
            Column {
                Text(especie.info.nombreComun, fontWeight = FontWeight.Bold)
                if (especie.info.nombreCientifico.isNotBlank()) {
                    Text(
                        especie.info.nombreCientifico,
                        style = MaterialTheme.typography.bodySmall,
                        fontStyle = FontStyle.Italic,
                        color = MaterialTheme.colorScheme.onSurfaceVariant,
                    )
                }
            }
        },
        text = {
            Column(verticalArrangement = Arrangement.spacedBy(9.dp)) {
                Text(
                    if (especie.esCapturada) "✓ Especie descubierta" else "Todavía no registrada en tus partes",
                    color = if (especie.esCapturada) Color(0xFF1D9E75) else MaterialTheme.colorScheme.onSurfaceVariant,
                    fontWeight = FontWeight.Bold,
                )
                DetailLine("Hábitat", especie.info.habitat)
                DetailLine("Mejor horario", especie.info.mejorHorario)
                DetailLine("Técnica", especie.info.tecnica)
                DetailLine("Tamaño", especie.info.tamano)
                DetailLine("Temporada", especie.info.temporada)
                if (especie.info.mejoresCarnadas.isNotEmpty()) {
                    DetailLine("Carnadas", especie.info.mejoresCarnadas.joinToString(", "))
                }

                especie.datosCaptura?.let { captura ->
                    Spacer(Modifier.height(4.dp))
                    Text("Mi registro", fontWeight = FontWeight.Bold)
                    DetailLine("Capturas", captura.totalCapturas.toString())
                    if (captura.mejorDiaCantidad > 0) {
                        DetailLine(
                            "Mejor jornada",
                            buildString {
                                append(captura.mejorDiaCantidad)
                                captura.mejorDiaFecha?.let { append(" · $it") }
                            },
                        )
                    }
                    if (captura.locaciones.isNotEmpty()) {
                        DetailLine("Lugares", captura.locaciones.joinToString(", "))
                    }
                }
            }
        },
        confirmButton = {
            TextButton(onClick = onDismiss) { Text("Cerrar") }
        },
    )
}

@Composable
private fun DetailLine(label: String, value: String) {
    if (value.isBlank()) return
    Column {
        Text(
            label,
            style = MaterialTheme.typography.labelSmall,
            color = MaterialTheme.colorScheme.primary,
            fontWeight = FontWeight.Bold,
        )
        Text(
            value,
            style = MaterialTheme.typography.bodySmall,
            color = MaterialTheme.colorScheme.onSurface,
        )
    }
}
